import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import LanguageContext from "../helper/LanguageContext";
import MaterialIcon from "./MaterialIcon";
import CompanyTrialEndedNote from "./CompanyTrialEndedNote";
import {
  isCompanyManagedKjuupIo,
  navigateToKjuupIoBilling,
  shouldShowCompanyTrialEndedNote,
} from "../utils/kjuupIoBillingNav";
import { resolveStripeFlags } from "../utils/stripeFeature";
import "./EmailEmptyState.css";

/**
 * Shared empty states for Messages / Room email surfaces.
 * Variants: noPlan | trialEnded | noAccounts
 *
 * Kjuup+ with no subscription: email UI is hidden upstream (entitlement gate).
 * After company trial ended: CompanyTrialEndedNote only for company admins with a seat.
 */
export default function EmailEmptyState({
  variant = "noPlan",
  onPrimary,
  primaryLabel,
  className,
  style,
}) {
  const { strings } = useContext(LanguageContext);
  const history = useHistory();
  const profile = useSelector((s) => s.profile);
  const subscription = useSelector((s) => s.subscription) || {};
  const { stripeUiEnabled } = resolveStripeFlags(profile, subscription);
  const companyManaged = isCompanyManagedKjuupIo(subscription);
  const showCompanyTrialEndedNote = shouldShowCompanyTrialEndedNote(subscription);

  if (variant === "noPlan" || variant === "trialEnded") {
    if (!stripeUiEnabled) {
      return null;
    }

    // Company trial fully ended — company admins with a seat only.
    if (showCompanyTrialEndedNote) {
      return <CompanyTrialEndedNote className={className} style={style} />;
    }

    // Kjuup+ with no booking / non-admin after trial: nothing Kjuup.io-related.
    if (companyManaged) {
      return null;
    }

    const ended = variant === "trialEnded";
    const title = ended
      ? strings.kjuup_io_trial_ended_title || "Your free trial has ended"
      : strings.email_no_accounts;
    const body = ended
      ? strings.kjuup_io_trial_ended_body ||
        "Subscribe to Kjuup.io to keep using email and other premium features."
      : null;
    const cta =
      primaryLabel ||
      (ended
        ? strings.kjuup_io_subscribe_to_continue ||
          strings.kjuup_io_subscribe_now ||
          "Subscribe now"
        : strings.kjuup_io_activate_to_connect_email ||
          "Activate Kjuup.io to connect email accounts");

    const handlePrimary = () => {
      if (onPrimary) {
        onPrimary();
        return;
      }
      navigateToKjuupIoBilling(history, subscription);
    };

    return (
      <div
        className={`email-empty-state email-empty-state--no-plan${className ? ` ${className}` : ""}`}
        data-testid="emailEmptyState"
        style={style}
      >
        <MaterialIcon icon="mail" size={48} color="#737373" className="email-empty-state__icon" />
        <h4 className="header-margin-gray">
          {title}
        </h4>
        {body ? (
          <p className="email-empty-state__body">
            {body}
          </p>
        ) : null}
        <button
          type="button"
          className="kjuup-io-activate-cta email-empty-state__cta"
          onClick={handlePrimary}
        >
          {cta}
        </button>
      </div>
    );
  }

  // noAccounts — settings CTA
  return (
    <div
      className={`email-empty-state email-empty-state--no-accounts${className ? ` ${className}` : ""}`}
      data-testid="emailEmptyState"
      style={style}
    >
      <MaterialIcon icon="mail" size={48} color="#737373" className="email-empty-state__icon" />
      <h4 className="header-margin-gray">{strings.email_no_accounts}</h4>
      <p className="email-empty-state__hint">
        {strings.email_no_accounts_hint}
      </p>
      {onPrimary ? (
        <button className="kj-btn kj-btn--primary kj-btn--email kj-btn--pill" type="button" onClick={onPrimary}>
          {primaryLabel || strings.email_go_to_settings}
        </button>
      ) : null}
    </div>
  );
}
